import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../contexts/ToastContext";
import {
  getKiedyNaboryCustomers,
  getMonthlySettlement,
  createSettlementOperation,
} from "../services/kiedynaborySettlementsApi";
import { CreateOperationDto } from "../types/kiedynaborySettlements";

// Query keys
export const SETTLEMENT_KEYS = {
  all: ["kiedynabory-settlements"] as const,
  customers: () => [...SETTLEMENT_KEYS.all, "customers"] as const,
  monthly: (month: string) => [...SETTLEMENT_KEYS.all, "monthly", month] as const,
};

/**
 * Hook to fetch KiedyNabory customers (tenants with kiedynabory brand)
 */
export const useKiedyNaboryCustomersQuery = () => {
  return useQuery({
    queryKey: SETTLEMENT_KEYS.customers(),
    queryFn: getKiedyNaboryCustomers,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch monthly settlement summary
 * @param month - Month in YYYY-MM format
 */
export const useMonthlySettlementQuery = (month: string) => {
  return useQuery({
    queryKey: SETTLEMENT_KEYS.monthly(month),
    queryFn: () => getMonthlySettlement(month),
    enabled: !!month && /^\d{4}-\d{2}$/.test(month),
    staleTime: 1000 * 60 * 2, // 2 minutes (data may change due to Stripe sync)
  });
};

/**
 * Hook to create a new settlement operation (cost or payout)
 * Admin-only mutation.
 */
export const useCreateOperationMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: CreateOperationDto) => createSettlementOperation(dto),
    onSuccess: (_, variables) => {
      // Invalidate the monthly settlement for the affected month
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.monthly(variables.month),
      });

      addToast({
        message: "Operacja została dodana",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error creating settlement operation:", error);
      addToast({
        message:
          error.response?.data?.message || "Błąd dodawania operacji",
        type: "error",
      });
    },
  });
};
